/**
 * File: seo.ts
 *
 * Keeps the live document head in step with the router. The values themselves
 * come from seo-meta.ts, which the build-time prerender shares, so a page's head
 * reads the same whether a crawler got the static copy or a browser navigated
 * to it inside the SPA.
 *
 * Every tag this module owns is marked `data-seo`, so a page swap can clear the
 * previous page's tags without touching the hand-written ones in index.html
 * (icons, fonts, theme-color, viewport). The tags that also exist statically —
 * title, description, canonical, the og:/twitter: pairs — are adopted on first
 * run rather than duplicated: `upsert` finds the existing element by selector,
 * stamps it, and from then on rewrites it in place.
 *
 * Without this every route in the SPA served one shared head: one title, one
 * description, and a canonical pointing at `/` — which told search engines that
 * all 14 articles were duplicates of the home page.
 */

import {
    OG_IMAGE_H,
    OG_IMAGE_W,
    SITE_NAME,
    jsonLdDocument,
    type PageSeo,
} from "./seo-meta";

const OWNED = "data-seo";

/**
 * Find (or create) the single element matching `selector` and mark it as ours.
 * `create` builds the element the first time when index.html has no such tag.
 */
function upsert(selector: string, create: () => HTMLElement): HTMLElement {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) {
        el = create();
        document.head.appendChild(el);
    }
    el.setAttribute(OWNED, "");
    return el;
}

function meta(attr: "name" | "property", key: string, content: string | undefined) {
    const selector = `meta[${attr}="${key}"]`;
    if (content === undefined) {
        // Not applicable to this page — drop it so a value from the previously
        // viewed page cannot linger (og:type=article on the home page, say).
        document.head.querySelector(selector)?.remove();
        return;
    }
    const el = upsert(selector, () => {
        const m = document.createElement("meta");
        m.setAttribute(attr, key);
        return m;
    });
    el.setAttribute("content", content);
}

function link(rel: string, href: string) {
    const el = upsert(`link[rel="${rel}"]`, () => {
        const l = document.createElement("link");
        l.setAttribute("rel", rel);
        return l;
    });
    el.setAttribute("href", href);
}

/**
 * Apply a page's SEO to the document head. Safe to call repeatedly — the article
 * view calls it twice, once with the route's placeholder values and again once
 * the article metadata has been fetched.
 */
export function applySeo(seo: PageSeo): void {
    document.title = seo.title;

    meta("name", "description", seo.description);
    meta("name", "robots", seo.robots);
    link("canonical", seo.canonical);

    meta("property", "og:type", seo.ogType);
    meta("property", "og:url", seo.canonical);
    meta("property", "og:title", seo.title);
    meta("property", "og:description", seo.description);
    meta("property", "og:image", seo.image);
    meta("property", "og:image:width", String(OG_IMAGE_W));
    meta("property", "og:image:height", String(OG_IMAGE_H));
    meta("property", "og:image:alt", seo.imageAlt);
    meta("property", "og:site_name", SITE_NAME);
    meta("property", "og:locale", "en_US");
    meta("property", "article:published_time", seo.published);

    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:url", seo.canonical);
    meta("name", "twitter:title", seo.title);
    meta("name", "twitter:description", seo.description);
    meta("name", "twitter:image", seo.image);
    meta("name", "twitter:image:alt", seo.imageAlt);

    // Structured data is rebuilt rather than patched: the node set differs per
    // page type, so editing one script in place would leave stale nodes behind.
    document.head
        .querySelectorAll('script[type="application/ld+json"]')
        .forEach((s) => s.remove());
    if (seo.jsonLd.length) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute(OWNED, "");
        script.textContent = jsonLdDocument(seo.jsonLd);
        document.head.appendChild(script);
    }
}
